import { DestinationInterface } from '../interfaces/Account/DestinationInterface'
import { BusinessUnitInterface } from '../interfaces/BusinessUnit/BusinessUnitInterface'
import { DeliveryEnum } from '../interfaces'
import { ShipmentGroup } from '../interfaces/Import/ShipmentGroupInterface'
import {
  consigneeCountryCode,
  PHONE_COUNTRY_CODE,
  PHONE_MAX_LOCAL_DIGITS,
  PHONE_MIN_LOCAL_DIGITS,
  splitDialCode,
} from './shipmentValidation'

export interface SimpleResponseLike<T> {
  didError: boolean
  errorMessage?: string
  model: T | null
}

export type GetBusinessUnitByTerritoryFn = (
  lng: number,
  lat: number
) => Promise<SimpleResponseLike<BusinessUnitInterface>>

export interface DestinationApplyResult {
  patch: Partial<ShipmentGroup>
  warning?: string
}

// Aviso único (fuente de verdad compartida por Review y AccordionRow) cuando unas
// coordenadas caen fuera de la zona de cobertura. No se limpian las coordenadas ni se
// resuelve por la ciudad del archivo: se le pide al operador que las corrija.
export const COORD_OUT_OF_ZONE_MSG =
  'Coordenadas fuera de zona de cobertura. Ingresa coordenadas o usa "Búsqueda en Google Maps:"'

/**
 * Mensaje del bloqueo inicial de carga masiva: qué le falta a la cuenta para poder
 * cargar guías (oficinas origen, servicios autorizados, cuenta para facturar).
 *
 * Enumera en español natural — "a", "a ni b", "a, b ni c" — porque las condiciones
 * pueden faltar en cualquier combinación y antes había un caso especial escrito a mano
 * solo para dos, que dejaba de cubrir el resto al agregar una tercera.
 * Devuelve '' con la lista vacía (nada que bloquear).
 */
export const missingSetupMessage = (missing: string[]): string => {
  if (!missing || missing.length === 0) return ''
  const listado =
    missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')} ni ${missing[missing.length - 1]}`
  return `No tiene ${listado}. Por favor comuníquese con el departamento de Soporte Técnico.`
}

// Venezuelan mobile area codes (without leading 0).
const MOBILE_PREFIXES = ['412', '414', '416', '422', '424', '426']

/**
 * Largo máximo de un teléfono LOCAL venezolano tal como lo guarda el editor: un
 * FIJO con su 0 inicial ("02121234567"). Es la frontera que separa "esto todavía
 * puede ser venezolano" de "acá sobra algo pegado".
 */
const VE_MAX_LOCAL_DIGITS = 11

/**
 * Forma canónica del teléfono LOCAL venezolano: **10 dígitos** — 3 del código de
 * área o de operadora + 7 del número (`2121234567`, `4141234567`).
 *
 * El `0` inicial con el que se marcan los fijos dentro del país (`0212 1234567`)
 * es un prefijo de marcación NACIONAL: no forma parte del número que viaja
 * junto al `+58`, y dejarlo adentro producía `+58 02121234567`, un dígito más
 * largo que el mismo número escrito como móvil.
 *
 * Antes esta función hacía lo contrario: le ANTEPONÍA un 0 a todo fijo que
 * llegara sin él. Eso dejaba a la carga masiva como la única pantalla del
 * sistema con esa convención — `ConsigneeFormV2`, Preshipment, `UserForm`,
 * `AccountAuthForm` e InventoryImport ya tratan al fijo como 10 dígitos sin 0
 * (`maxLength={codePhoneNumber !== 'FIJO' ? 7 : 10}`).
 */
export const normalizePhone = (raw: string): string => {
  let digits = (raw || '').replace(/\D/g, '')
  if (!digits) return ''

  // Código de país pegado al número ("+58 412 1234567", "+58 04121234567"): un
  // local nunca pasa de 10 dígitos, así que un 58 adelante con más que eso es
  // el +58 y se descarta.
  if (digits.length > 10 && digits.startsWith('58')) {
    digits = digits.substring(2)
  }

  // El 0 de marcación nacional se va, venga de un móvil ("0412…") o de un fijo
  // ("0212…"). Lo que queda es el número tal como lo espera el backend.
  if (digits.startsWith('0')) digits = digits.substring(1)

  // Sin recortes ni relleno: si al final no son 10 dígitos, lo dice el
  // validador con un mensaje que explica qué falta.
  return digits
}

// ---------------------------------------------------------------------------
// Teléfonos internacionales en la carga masiva
//
// El archivo del aliado puede traer destinatarios de fuera de Venezuela. El
// backend los acepta (valida con libphonenumber contra el país del prefijo),
// pero TODO el manejo de teléfono del front asumía +58: `normalizePhone` le
// antepone un 0 a cualquier forma que no reconozca, y el validador exige
// operadora venezolana. El resultado era que un "+57 3001234567" quedaba como
// "0573001234567" y la fila no se podía enviar nunca.
//
// La fila guarda DOS datos: los dígitos locales (`consigneePhone`) y el código
// de país (`consigneePhoneCountryCode`). Estas funciones son la única puerta
// entre el texto crudo del Excel / del portapapeles y ese par.
// ---------------------------------------------------------------------------

export interface PhoneCellParts {
  /** Prefijo internacional, siempre con `+` ("+58", "+57"). */
  countryCode: string
  /** Dígitos locales, ya en la convención del país (VE: la de `normalizePhone`). */
  phone: string
  /**
   * El texto DECÍA su país ("+57 300…"). Sin esto, `countryCode: '+58'` es
   * ambiguo entre "es venezolano" y "no se sabe, se asume el default", y quien
   * pega un número local en una fila colombiana la vería saltar a Venezuela.
   */
  explicitCountry: boolean
  /**
   * La celda TIENE forma de número internacional pero sin el `+` que lo
   * confirme, así que se interpretó como venezolana (que es lo conservador) y
   * hay que avisarle al operador para que elija el país. Ver `internationalCellWarning`.
   */
  internationalHint: boolean
}

/**
 * `+CC` (o `00CC`) seguido de separador y el resto del número. Exige el
 * separador a propósito: sin él ("+573001234567") no hay forma de saber dónde
 * termina el prefijo — 1, 2 o 3 dígitos son todos códigos de país válidos — y
 * adivinar mal manda la guía al backend con un número mutilado.
 */
const INTL_PREFIX_RE = /^(?:\+|00)(\d{1,3})[\s.-]+(.+)$/

/** Aviso de fila para una celda con pinta de internacional que perdió el `+`. */
export const internationalCellWarning = (rawCell: string): string =>
  `El teléfono "${(rawCell || '').trim()}" parece un número internacional sin el "+". ` +
  'Se cargó como venezolano: elegí el país en el campo "País" y verificá los dígitos.'

/**
 * Parte la celda de teléfono del Excel en código de país + dígitos locales.
 *
 * Solo se reconoce el país cuando la celda lo dice explícitamente ("+57 300…",
 * "0034 612…"). Cualquier otra cosa cae al camino de siempre (Venezuela), que es
 * el comportamiento que ya tenía el sistema.
 */
export const splitPhoneCountryCode = (rawCell: string): PhoneCellParts => {
  // La exportación de corrección antepone `'` a los valores que empiezan por `+`
  // (guarda anti-fórmula de Excel). Al re-subir el archivo la comilla vuelve, y
  // sin quitarla la celda no matchearía ningún prefijo.
  const raw = (rawCell || '').trim().replace(/^'/, '').trim()
  if (!raw)
    return {
      countryCode: PHONE_COUNTRY_CODE,
      phone: '',
      explicitCountry: false,
      internationalHint: false,
    }

  const match = raw.match(INTL_PREFIX_RE)
  if (match) {
    const countryCode = `+${match[1]}`
    const digits = match[2].replace(/\D/g, '')
    return {
      countryCode,
      explicitCountry: true,
      // `normalizePhone` SOLO sabe de Venezuela; correrla sobre un número
      // extranjero le pone un 0 adelante y lo rompe.
      //
      // Los dígitos van COMPLETOS aunque se pasen del máximo: recortarlos acá
      // produciría un número distinto del que mandó el aliado y, encima, válido
      // — la guía saldría mal sin que nadie lo note. El largo lo marca en rojo
      // `consigneePhoneError`, que es donde el operador lo puede ver y corregir.
      phone: countryCode === PHONE_COUNTRY_CODE ? normalizePhone(digits) : digits,
      internationalHint: false,
    }
  }

  // El parser lee las celdas con `raw: true`: una celda numérica "+573001234567"
  // llega como 573001234567, sin `+`. Se detecta ACÁ, sobre el texto crudo —
  // después de `normalizePhone` es imposible, porque el 0 que antepone disfraza
  // el número de FIJO venezolano.
  //
  // El corte está en 11 y no en 12: un local venezolano llega como mucho a 11
  // dígitos y SIEMPRE empieza por 0 (FIJO) o por un prefijo de operadora
  // (móvil), así que 11 dígitos sin 0 y sin prefijo móvil ya no puede ser
  // venezolano. Con 12 quedaban afuera Chile (+56), España (+34), Perú (+51),
  // Panamá (+507) y Bolivia (+591), donde prefijo + local suman justo 11: el
  // operador no veía ningún aviso, solo un mensaje sobre operadoras
  // venezolanas que lo mandaba a borrar dígitos buenos.
  //
  // El prefijo móvil se excluye a propósito: un "41412345678" es un móvil con
  // un dígito de más —un tipeo—, no un número internacional.
  const digits = raw.replace(/\D/g, '')
  const internationalHint =
    digits.length >= 11 &&
    digits.length <= PHONE_MAX_LOCAL_DIGITS &&
    !digits.startsWith('0') &&
    !digits.startsWith('58') &&
    !MOBILE_PREFIXES.includes(digits.slice(0, 3))

  return {
    countryCode: PHONE_COUNTRY_CODE,
    phone: normalizePhone(raw),
    explicitCountry: false,
    internationalHint,
  }
}

/**
 * Cambio de país de una fila: devuelve el patch con el código nuevo y los
 * dígitos ya reescritos a la convención de ese país.
 *
 * Es la ÚNICA puerta al cambio de país — la usan el selector de la fila, el
 * pegado y la barra de acciones masivas. Un `{ ...g, ...patch }` a pelo desde la
 * barra dejaría el "0573001234567" tal cual y el backend rechazaría las 150
 * guías del lote.
 */
export const changeCountryCode = (
  group: Pick<ShipmentGroup, 'consigneePhone' | 'consigneePhoneCountryCode'>,
  code: string,
): Partial<ShipmentGroup> => {
  const next = code || PHONE_COUNTRY_CODE
  const previous = consigneeCountryCode(group)
  const digits = (group.consigneePhone || '').replace(/\D/g, '')

  if (next === PHONE_COUNTRY_CODE)
    return {
      consigneePhoneCountryCode: next,
      consigneePhone: normalizePhone(digits),
    }

  // La fila YA estaba en un país que no es Venezuela: sus dígitos son locales y
  // nadie les pegó nada. Cambiar de país acá es solo cambiar la etiqueta.
  //
  // Sin esta salida temprana la heurística de abajo corría de nuevo sobre
  // dígitos ya limpios y se comía los primeros: la barra de acciones masivas la
  // aplica sin mirar el país actual, y el pegado la llamaba sobre unos dígitos
  // que `splitPhoneCountryCode` ACABABA de separar de su prefijo. Pegar
  // "+55 55987654321" (móvil real de Brasil, DDD 55) quedaba en "987654321",
  // que además pasa la validación: la guía salía con otro número y en silencio.
  if (previous !== PHONE_COUNTRY_CODE)
    return { consigneePhoneCountryCode: next, consigneePhone: digits }

  // Venezuela → otro país: el 0 inicial es la marca de FIJO venezolano, no un
  // dígito del número.
  let local = digits.startsWith('0') ? digits.slice(1) : digits

  // …y si lo que queda EMPIEZA por el prefijo del país elegido, ese prefijo se
  // coló dentro del número (celda "+57 300…" leída como 573001234567).
  //
  // Solo se despega cuando el valor NUNCA pudo haber sido un número venezolano:
  // un local de acá llega como mucho a 11 dígitos con su 0, o sea 10 sin él, así
  // que de 12 en adelante sobra algo. Con el corte en 10 se mutilaban números
  // extranjeros legítimos de 11 dígitos que arrancan igual que su prefijo
  // (Brasil, Japón). Lo que quede por debajo del corte y esté mal lo rechaza el
  // backend con un mensaje visible, que es mejor que corromperlo callado.
  const codeDigits = next.replace(/\D/g, '')
  if (
    codeDigits &&
    local.startsWith(codeDigits) &&
    local.length > VE_MAX_LOCAL_DIGITS &&
    local.length - codeDigits.length >= PHONE_MIN_LOCAL_DIGITS
  )
    local = local.slice(codeDigits.length)

  // Sin recorte al máximo: un número demasiado largo se marca en rojo, no se
  // convierte en silencio en otro número que sí pasa.
  return { consigneePhoneCountryCode: next, consigneePhone: local }
}

/** Forma mínima del teléfono de una cuenta (favorito, cliente). */
export interface AccountPhoneLike {
  countryPhoneAccessCode?: string | null
  phoneNumber?: string | null
}

/**
 * Teléfono del destinatario a partir del primer teléfono de una cuenta
 * (favorito). ÚNICA fuente: el código sale de `countryPhoneAccessCode` y los
 * dígitos solo se normalizan a la convención venezolana cuando ese código es
 * +58. Antes se llamaba `normalizePhone` a secas y un favorito colombiano
 * entraba a la fila ya roto.
 */
export const consigneePhoneFromAccount = (
  phone: AccountPhoneLike | null | undefined,
): { consigneePhone: string; consigneePhoneCountryCode: string } => {
  const { dial, extraDigits } = splitDialCode(phone?.countryPhoneAccessCode)
  const digits = extraDigits + (phone?.phoneNumber || '').replace(/\D/g, '')
  return {
    consigneePhoneCountryCode: dial,
    // Sin recorte: un favorito con un teléfono imposible se marca en rojo en la
    // fila (y el aviso le dice al operador que lo corrija en Destinos
    // favoritos), no se convierte en otro número que sí pasa la validación.
    consigneePhone: dial === PHONE_COUNTRY_CODE ? normalizePhone(digits) : digits,
  }
}

// Dropdown values in the Excel template are generated as "${buCode} - ${buArea}"
// (space-hyphen-space). Split on that exact separator so buCodes containing internal
// hyphens (e.g. "1102-CC") survive the parse. Fall back to the trimmed input when no
// separator exists.
export const STORE_CODE_SEPARATOR = ' - '
export const cleanStoreCode = (str: string): string => {
  if (!str) return ''
  const sepIdx = str.indexOf(STORE_CODE_SEPARATOR)
  if (sepIdx !== -1) return str.substring(0, sepIdx).trim()
  return str.trim()
}

// Build the destination patch from a favorite. If isHomeDelivery + lat/lng valid,
// re-validate territory via getBusinessUnitByTerritory and override store/city/postal
// with territory result. If territory invalid, keep favorito data and surface warning.
export const applyDestinationFromFavorite = async (
  fav: DestinationInterface,
  getTerritory?: GetBusinessUnitByTerritoryFn
): Promise<DestinationApplyResult> => {
  const acc = fav.account
  const loc = fav.location
  const bu = fav.businessUnit
  const patch: Partial<ShipmentGroup> = {
    consigneeDocType:
      acc?.taxIdentificationTypeID == null ? '' : String(acc.taxIdentificationTypeID),
    consigneeDocNumber: acc?.identificationNumber || '',
    consigneeName: acc?.accountFullName || '',
    ...consigneePhoneFromAccount(acc?.listAccountPhone?.[0]),
    consigneeEmail: acc?.listAccountEmail?.[0]?.email || '',
    destinationAddress: loc?.address || '',
    postalCode: loc?.postalCode || '',
    // V3+: lat/lng only apply to home delivery. Office favorites stay coordinate-free.
    latitude: fav.isHomeDelivery ? loc?.coordinates?.lat ?? null : null,
    longitude: fav.isHomeDelivery ? loc?.coordinates?.lng ?? null : null,
    storeCode: bu?.buCode || '',
    destinationCity: bu?.location?.locationCode || '',
    deliveryType: fav.isHomeDelivery ? DeliveryEnum.DELIVERY : DeliveryEnum.OFFICE,
    favoriteDestinationId: fav.id || acc?.id || '',
    // H.3d: punto de referencia from the favorite (operator's own note like
    // "Frente al parque"). Only meaningful for home delivery — office BU lookup
    // doesn't carry an address reference.
    addressReference: fav.isHomeDelivery ? loc?.reference || '' : '',
  }

  if (
    fav.isHomeDelivery &&
    patch.latitude != null &&
    patch.longitude != null &&
    getTerritory
  ) {
    try {
      const res = await getTerritory(patch.longitude, patch.latitude)
      if (!res.didError && res.model) {
        patch.storeCode = res.model.buCode || patch.storeCode
        patch.destinationCity =
          res.model.location?.locationCode || patch.destinationCity
        patch.postalCode = res.model.location?.postalCode || patch.postalCode
      } else {
        return {
          patch,
          warning: `Favorito ${acc?.accountFullName || ''}: territorio cambió, usando datos del favorito.`,
        }
      }
    } catch (_) {
      // Silent — keep favorito data
    }
  }

  return { patch }
}

export const clearConsigneeFields = (): Partial<ShipmentGroup> => ({
  consigneeDocType: '',
  consigneeDocNumber: '',
  consigneeName: '',
  consigneePhone: '',
  consigneePhoneCountryCode: PHONE_COUNTRY_CODE,
  consigneeEmail: '',
  destinationAddress: '',
  postalCode: '',
  latitude: null,
  longitude: null,
  storeCode: '',
  destinationCity: '',
  favoriteDestinationId: undefined,
})

// Phone normalize-on-paste — accepts:
//   "0414 1234567"   "0414-1234567"   "04141234567"
//   "414 1234567"    "414-1234567"    "4141234567"
// Returns { code: 3-digit, number: 7-digit } if matched against prefixList, else null.
const PHONE_PASTE_REGEX = /^[\s-]*(\d{3,4})[\s-]?(\d{7})$/

export const tryParsePastedPhone = (
  raw: string,
  prefixList: { value: string }[]
): { code: string; number: string } | null => {
  const cleaned = (raw || '').trim()
  const match = cleaned.match(PHONE_PASTE_REGEX)
  if (!match) return null
  const [, codeRaw, number] = match
  // Try matching with and without leading 0
  const stripped = codeRaw.length === 4 && codeRaw.startsWith('0')
    ? codeRaw.substring(1)
    : codeRaw
  const found = prefixList.find((p) => p.value === stripped)
  if (!found) return null
  return { code: found.value, number }
}

// Tolerant parser for "RIF — Nombre" / "RIF – Nombre" / "RIF - Nombre".
// Accepts em-dash (—), en-dash (–), or hyphen (-) as separator.
// Returns the RIF part (everything before first separator) trimmed.
export const parseFavoriteIdentifier = (display: string): string => {
  if (!display) return ''
  // Split on space + separator + space (preferred) OR any of the dashes alone
  const sepRegex = /\s+[—–]\s+|\s+-\s+/
  const parts = display.split(sepRegex)
  if (parts.length > 1) return parts[0].trim()
  // Fallback: no clear separator, return whole string
  return display.trim()
}

// H.4: defensive coordinate extractor for SheetJS cell values. Handles:
//   - Number (the common case when Excel parses comma-decimal locale strings)
//   - string with comma or period decimal (es-VE vs en-US locale)
//   - Excel rich cell objects: { result: Number } or { value: ... } (formulas)
//   - null/undefined/empty → null (caller decides what to do)
// Returns null only for genuinely unparseable input. Preserves 0,0 (equator/meridian).
export const extractCoord = (raw: unknown): number | null => {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'object') {
    const obj = raw as { result?: unknown; value?: unknown }
    if (typeof obj.result === 'number' && Number.isFinite(obj.result)) {
      return obj.result
    }
    if ('value' in obj) return extractCoord(obj.value)
    return null
  }
  const s = String(raw).trim().replace(',', '.')
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Reverse-geocode lat/lng to a customer-facing address via Google Maps Geocoder.
// getBusinessUnitByTerritory returns the TEALCA store's address (warehouse), not
// the customer's location at the coords — so for autofilling destinationAddress
// we need a true reverse-geocode. Mirrors the path in FormMaps.handleCoordsSelect:
// prefer street_address/route/intersection results; fall back to first result.
// Returns null when google.maps isn't loaded, no results, or on error — caller
// should fall back to keeping the prior address.
export const reverseGeocodeCoords = (
  lat: number,
  lng: number
): Promise<{ address: string; postalCode: string } | null> => {
  return new Promise((resolve) => {
    const g = (window as any).google
    if (!g?.maps?.Geocoder) {
      resolve(null)
      return
    }
    try {
      const geocoder = new g.maps.Geocoder()
      geocoder.geocode(
        { location: new g.maps.LatLng(lat, lng) },
        (results: any[] | null, status: string) => {
          if (status !== 'OK' || !results || results.length === 0) {
            resolve(null)
            return
          }
          const preferred = results.filter((r) =>
            r.types?.includes('street_address') ||
            r.types?.includes('route') ||
            r.types?.includes('intersection')
          )
          const selected = preferred[0] ?? results[0]
          const postalCode =
            selected.address_components?.find((c: any) =>
              c.types?.includes('postal_code')
            )?.long_name ?? ''
          resolve({
            address: selected.formatted_address ?? '',
            postalCode,
          })
        }
      )
    } catch (_) {
      resolve(null)
    }
  })
}

// Match a destination against a query against displayName, accountFullName,
// identificationNumber, or first phone number. Used by FavoriteDestinationPicker
// and AccountSearch. Empty query returns true (full list).
export const filterDestinationByQuery = (
  d: DestinationInterface,
  query: string
): boolean => {
  const q = (query || '').trim().toLowerCase()
  if (!q) return true
  const acc = d.account
  if (!acc) return false
  if ((acc.displayName || '').toLowerCase().includes(q)) return true
  if ((acc.accountFullName || '').toLowerCase().includes(q)) return true
  if ((acc.identificationNumber || '').toLowerCase().includes(q)) return true
  const phone = acc.listAccountPhone?.[0]?.phoneNumber || ''
  if (phone.includes(q)) return true
  return false
}
